import React, {
  Dispatch,
  FC,
  KeyboardEvent,
  SetStateAction,
  useState,
} from 'react';
import PlusIcon from 'assets/svg/PlusIcon';
import CarIcon from 'assets/svg/CarIcon';
import ZapIcon from 'assets/svg/ZapIcon';
import DropletIcon from 'assets/svg/DropletIcon';
import BikeIcon from 'assets/svg/BikeIcon';
import { BiScan, BiSearch } from 'react-icons/bi';
import { TravelMode } from 'types/bundleTypes';
import './ShiftCalendar.scss';
import clsx from 'clsx';
import { IShift } from './shiftApi';
import {
  CourierVehicleType,
  countBikeCouriers,
  countCargoBikeCouriers,
  countElectricCouriers,
  countFossilCouriers,
  countHybridCouriers,
  CourierCollection,
  getBookedCourierIds,
  JobType,
  ShiftOverviewStatus,
} from './utils';
import CarElectricIcon from 'assets/svg/CarElectricIcon';
import CarFossilIcon from 'assets/svg/CarFossilIcon';
import CarHybridIcon from 'assets/svg/CarHybridIcon';
import { TooltipComponent } from 'ReusableComponents/Tooltip/TooltipComponent';
import CargoBikeIcon from 'assets/svg/CargoBikeIcon';
import { Dayjs } from 'dayjs';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { MdContentCopy } from 'react-icons/md';
import { RadioComponent } from '../../ReusableComponents/Radio/RadioComponent';
import { CheckboxComponent } from '../../ReusableComponents/Checkbox/CheckboxComponent';
import { ButtonComponent } from '../../ReusableComponents/Button/ButtonComponent';
import Dropdown from '../../components/dropdown/Dropdown';
import Swal, { SweetAlertResult } from 'sweetalert2';

export const CarTypeIcon: FC<{ fossil?: boolean; electric?: boolean }> = ({
  fossil,
  electric,
}) => {
  return (
    <div className="newShiftCalendar__carTypeIcon">
      <CarIcon className="newShiftCalendar__carIcon" />
      <div className="newShiftCalendar__carTypeIcon__type">
        {electric && <ZapIcon />}
        {fossil && <DropletIcon />}
      </div>
    </div>
  );
};

export const Separator: FC = () => (
  <div className="newShiftCalendar__day__header__separator">
    <hr />
  </div>
);

export const CalendarCustomButton: FC<{
  text: string;
  onClick: () => void;
}> = ({ text, onClick }) => (
  <div className="newShiftCalendar__customButton" onClick={onClick}>
    <PlusIcon className="newShiftCalendar__customButton__icon" />
    <p>{text}</p>
  </div>
);

export const RoleIcons: FC<{
  className?: string;
  vehicleType?: TravelMode;
  isElectric?: boolean;
  isScanner?: boolean;
}> = ({ className, vehicleType, isElectric, isScanner }) => (
  <div className={clsx('newShiftCalendar__roleIcons', className)}>
    {vehicleType === TravelMode.BIKE ? (
      <BikeIcon />
    ) : vehicleType === TravelMode.CAR && isElectric ? (
      <CarTypeIcon electric />
    ) : vehicleType === TravelMode.CAR && !isElectric ? (
      <CarTypeIcon fossil />
    ) : (
      <div className="newShiftCalendar__roleIcons__placeholder" />
    )}
    <BiScan
      style={{
        height: '12px',
        visibility: isScanner ? 'visible' : 'hidden',
      }}
    />
  </div>
);

export const PlannerDates: FC<{
  startDate: Dayjs;
  endDate: Dayjs;
  setStartDate: Dispatch<SetStateAction<Dayjs>>;
  setEndDate: Dispatch<SetStateAction<Dayjs>>;
  duplicateWeek?: () => void;
}> = ({ startDate, endDate, setStartDate, setEndDate, duplicateWeek }) => {
  const shouldDuplicate = async () => {
    const result: SweetAlertResult<string> = await Swal.fire({
      icon: 'question',
      title: 'Confirm shifts duplication',
      showCancelButton: true,
      confirmButtonText: 'Ok',
      cancelButtonText: 'Cancel',
    });
    if (result.isConfirmed) {
      duplicateWeek();
    }
  };

  return (
    <>
      <div className="shiftPlanner__top__week__current">
        <h1>Week {startDate.isoWeek()}</h1>
        {duplicateWeek ? (
          <TooltipComponent
            className="shiftPlanner__top__week__current__duplicate"
            id="courier name"
            title="Duplicate to next week"
            placement="top-start"
          >
            <MdContentCopy onClick={shouldDuplicate} />
          </TooltipComponent>
        ) : null}
      </div>
      <div className="shiftPlanner__top__week__dates">
        <FaChevronLeft
          onClick={() => {
            setStartDate(oldState => oldState.subtract(1, 'week'));
            setEndDate(oldState => oldState.subtract(1, 'week'));
          }}
        />
        <div className="shiftPlanner__top__week__dates__text">
          <p>{startDate.format('dddd Do MMMM')}</p>
          <p className="shiftPlanner__top__week__dates__text__hyphen">-</p>
          <p>{endDate.format('dddd Do MMMM')}</p>
        </div>
        <FaChevronRight
          onClick={() => {
            setStartDate(oldState => oldState.add(1, 'week'));
            setEndDate(oldState => oldState.add(1, 'week'));
          }}
        />
      </div>
    </>
  );
};

export const TransportOverview: FC<{
  className?: string;
  couriers: CourierCollection;
  shifts: IShift[];
  showAsPercentages?: boolean;
}> = ({ className, couriers, shifts, showAsPercentages }) => {
  const bookedCourierIds = getBookedCourierIds(shifts);
  const bookedCouriersCount = bookedCourierIds.length || Infinity;

  const elCouriersCount = countElectricCouriers(bookedCourierIds, couriers);
  const elCouriersPercent = (100 * elCouriersCount) / bookedCouriersCount;

  const bikeCouriersCount = countBikeCouriers(bookedCourierIds, couriers);
  const bikeCouriersPercent = (100 * bikeCouriersCount) / bookedCouriersCount;

  const cargoBikeCouriersCount = countCargoBikeCouriers(
    bookedCourierIds,
    couriers
  );
  const cargoBikeCouriersPercent =
    (100 * cargoBikeCouriersCount) / bookedCouriersCount;

  const hybridCouriersCount = countHybridCouriers(bookedCourierIds, couriers);
  const hybridCouriersPercent =
    (100 * hybridCouriersCount) / bookedCouriersCount;

  const fossilCouriersCount = countFossilCouriers(bookedCourierIds, couriers);
  const fossilCouriersPercent =
    (100 * fossilCouriersCount) / bookedCouriersCount;

  return (
    <div className={className ?? ''}>
      <TooltipComponent id="transport-electric-tt" title="Electric">
        <CarElectricIcon />
        <p>
          {showAsPercentages
            ? `${elCouriersPercent.toFixed()}%`
            : elCouriersCount}
        </p>
      </TooltipComponent>
      <TooltipComponent id="transport-petrol-tt" title="Petrol">
        <CarFossilIcon />
        <p>
          {showAsPercentages
            ? `${fossilCouriersPercent.toFixed()}%`
            : fossilCouriersCount}
        </p>
      </TooltipComponent>
      <TooltipComponent id="transport-hybrid-tt" title="Hybrid">
        <CarHybridIcon />
        <p>
          {showAsPercentages
            ? `${hybridCouriersPercent.toFixed()}%`
            : hybridCouriersCount}
        </p>
      </TooltipComponent>
      <TooltipComponent id="transport-cargobike-tt" title="Cargo Bike">
        <CargoBikeIcon />
        <p>
          {showAsPercentages
            ? `${cargoBikeCouriersPercent.toFixed()}%`
            : cargoBikeCouriersCount}
        </p>
      </TooltipComponent>
      <TooltipComponent id="transport-bike-tt" title="Bike">
        <BikeIcon />
        <p>
          {showAsPercentages
            ? `${bikeCouriersPercent.toFixed()}%`
            : bikeCouriersCount}
        </p>
      </TooltipComponent>
    </div>
  );
};

const ShiftStatusFilter: FC<{
  selectedShiftStatus: ShiftOverviewStatus;
  setSelectedShiftStatus: Dispatch<SetStateAction<ShiftOverviewStatus>>;
}> = ({ selectedShiftStatus, setSelectedShiftStatus }) => {
  const displayedShiftStatusList = [
    ShiftOverviewStatus.BOOKED,
    ShiftOverviewStatus.REQUESTED,
    ShiftOverviewStatus.DECLINED,
    ShiftOverviewStatus.CANCELED,
    ShiftOverviewStatus.DISPATCH_DECLINED,
    ShiftOverviewStatus.ALL,
  ];

  return (
    <div className="shiftPlanner__top__shift-filter__other__status-dropdown">
      <Dropdown
        placeholder={`Status: ${selectedShiftStatus}`}
        names={displayedShiftStatusList}
        className="status-dropdown-wrapper"
        onChange={event => {
          const shiftStatus = event as string;
          const selectedStatus = displayedShiftStatusList.find(
            status => status === shiftStatus
          );
          setSelectedShiftStatus(selectedStatus);
        }}
      />
    </div>
  );
};

const VehicleFilter: FC<{
  setSelectedCarType: Dispatch<SetStateAction<CourierVehicleType>>;
  selectedCarType: CourierVehicleType;
}> = ({ setSelectedCarType, selectedCarType }) => {
  const displayedVehicleTypes = {
    Electric: CourierVehicleType.ELECTRIC,
    Hybrid: CourierVehicleType.HYBRID,
    Petrol: CourierVehicleType.PETROL,
    Diesel: CourierVehicleType.DIESEL,
    'Normal bike': CourierVehicleType.NORMAL_BIKE,
    'Cargo bike': CourierVehicleType.CARGO_BIKE,
    All: CourierVehicleType.ALL,
  };

  const displayedCarType = Object.keys(displayedVehicleTypes).find(
    key => displayedVehicleTypes[key] === selectedCarType
  );

  return (
    <div className="shiftPlanner__top__shift-filter__other__vehicle-dropdown">
      <Dropdown
        placeholder={`Vehicle: ${displayedCarType}`}
        names={Object.keys(displayedVehicleTypes)}
        onChange={event => {
          const carType = event as string;
          const selectedCarType = displayedVehicleTypes[carType];
          setSelectedCarType(selectedCarType);
        }}
      />
    </div>
  );
};

export const ShiftFilterOptions: FC<{
  selectedJobType: JobType;
  onlyExtraWork: boolean;
  setSelectedJobType: Dispatch<SetStateAction<JobType>>;
  setOnlyExtraWork: Dispatch<SetStateAction<boolean>>;
  setNameSearch: Dispatch<SetStateAction<string>>;
  selectedShiftStatus: ShiftOverviewStatus;
  setSelectedShiftStatus: Dispatch<SetStateAction<ShiftOverviewStatus>>;
  setSelectedCarType: Dispatch<SetStateAction<CourierVehicleType>>;
  selectedCarType: CourierVehicleType;
}> = ({
  selectedJobType,
  onlyExtraWork,
  setSelectedJobType,
  setOnlyExtraWork,
  setNameSearch,
  selectedShiftStatus,
  setSelectedShiftStatus,
  setSelectedCarType,
  selectedCarType,
}) => {
  const [searchString, setSearchString] = useState<string>('');

  const handleEnterPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') setNameSearch(searchString.toLowerCase());
  };

  return (
    <div className="shiftPlanner__top__shift-filter">
      <h3>Filter by job type</h3>
      <div className="shiftPlanner__top__shift-filter__radio">
        <RadioComponent
          checked={selectedJobType === JobType.ALL}
          id="job-type-all"
          name="job-type-filter"
          onChange={() => setSelectedJobType(JobType.ALL)}
          value="all"
          label={{ node: 'All' }}
        />
        <RadioComponent
          checked={selectedJobType === JobType.COURIER}
          id="job-type-courier"
          name="job-type-filter"
          onChange={() => setSelectedJobType(JobType.COURIER)}
          value="courier"
          label={{ node: 'Courier' }}
        />
        <RadioComponent
          checked={selectedJobType === JobType.SORTER}
          id="job-type-sorter"
          name="job-type-filter"
          onChange={() => setSelectedJobType(JobType.SORTER)}
          value="sorter"
          label={{ node: 'Sorter' }}
        />
      </div>
      <div className="shiftPlanner__top__shift-filter__checkbox">
        <CheckboxComponent
          checked={onlyExtraWork}
          name="show-with-extra-work"
          handleChange={() => setOnlyExtraWork(oldState => !oldState)}
          label="Show shifts with extra work"
        />
      </div>
      <div className="shiftPlanner__top__shift-filter__search">
        <input
          className="shiftPlanner__top__shift-filter__search__input"
          placeholder="Search name"
          onChange={e => setSearchString(e.currentTarget.value)}
          onKeyPress={handleEnterPress}
        />
        <ButtonComponent
          className="shiftPlanner__top__shift-filter__search__button"
          role="button"
          style={{ backgroundColor: '#666666' }}
          tabIndex={0}
          onClick={() => setNameSearch(searchString.toLowerCase())}
        >
          <BiSearch />
        </ButtonComponent>
      </div>
      <div className="shiftPlanner__top__shift-filter__other">
        <ShiftStatusFilter
          selectedShiftStatus={selectedShiftStatus}
          setSelectedShiftStatus={setSelectedShiftStatus}
        />
        <VehicleFilter
          setSelectedCarType={setSelectedCarType}
          selectedCarType={selectedCarType}
        />
      </div>
    </div>
  );
};
